import { Injectable, TemplateRef, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { MbsPopupType } from '../utils';
import Toast from './toast.model';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private router = inject(Router);

  private queue = new Subject<{ toast: Toast; action: 'add' | 'remove' }>();
  private keepAfterRouteChange = false;
  private toastsDisabled = false;

  constructor() {
    const router = this.router;

    // clear alert messages on route change unless 'keepAfterRouteChange' flag is true
    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.keepAfterRouteChange) {
          this.keepAfterRouteChange = false;
        } else {
          // TODO: need refactored. Create empty toast with default setting
          // this.clearById();
        }
      }
    });
  }

  disableToasts(): void {
    this.toastsDisabled = true;
  }

  enableToasts(): void {
    this.toastsDisabled = false;
  }

  /**
   * Subscribe to toasts.
   * @return {Observable}
   */
  getQueue(): Observable<{ toast: Toast; action: 'add' | 'remove' }> {
    return this.queue.asObservable();
  }

  /**
   * Alias for calling Toast by type: `MbsPopupType.success`
   * @default text for `content` is "Success"
   *
   * @param {string} message
   * @param {string} title
   */
  success(message?: string | TemplateRef<any>, title?: string): void {
    this.toast(
      new Toast({
        header: title,
        icon: true,
        content: message || 'Success',
        type: MbsPopupType.success
      })
    );
  }

  /**
   * Alias for calling Toast by type: `MbsPopupType.danger`
   * @default text for `content` is "Sorry, something went wrong"
   *
   * @param {string} message
   * @param {string} title
   */
  error(message?: string | TemplateRef<any>, title?: string): void {
    this.toast(
      new Toast({
        header: title,
        icon: true,
        content: message || 'Sorry, something went wrong',
        type: MbsPopupType.danger
      })
    );
  }

  /**
   * Alias for calling Toast by type: `MbsPopupType.info`
   *
   * @param {string} message
   * @param {string} title
   */
  info(message: string | TemplateRef<any>, title?: string): void {
    this.toast(
      new Toast({
        header: title,
        icon: true,
        content: message,
        type: MbsPopupType.info
      })
    );
  }

  /**
   * alias for calling Toast by type: `MbsPopupType.warning`
   *
   * @param {string} message
   * @param {string} title
   */
  warn(message: string | TemplateRef<any>, title?: string): void {
    this.toast(
      new Toast({
        header: title,
        icon: true,
        content: message,
        type: MbsPopupType.warning
      })
    );
  }

  /**
   * Method for calling custom Toast
   *
   * @param {Toast} toast
   */
  toast(toast: Toast): void {
    if (this.toastsDisabled) return;

    this.keepAfterRouteChange = toast.keepAfterRouteChange;
    this.queue.next({ toast, action: 'add' });
  }

  /**
   * Remove toast
   * @param {Toast} toast
   */
  remove(toast: Toast): void {
    this.queue.next({ toast, action: 'remove' });
  }
}
